import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

export type HeaderPosition = 'top' | 'bottom' | 'floating' | 'minimized'
export type HeaderMode = 'top-expanded' | 'top-collapsed' | 'bottom' | 'floating' | 'minimized'

interface HeaderPrefsContextType {
  position: HeaderPosition
  setPosition: (position: HeaderPosition) => void
  mode: HeaderMode
  setMode: (mode: HeaderMode) => void
  /** Reset to default (top expanded) */
  reset: () => void
}

const HeaderPrefsContext = createContext<HeaderPrefsContextType | undefined>(undefined)

const STORAGE_KEY = 'lukhas_header_mode'
const DEFAULT_POSITION: HeaderPosition = 'top'
const DEFAULT_MODE: HeaderMode = 'top-expanded'

function loadPrefs(): { position: HeaderPosition; mode: HeaderMode } {
  if (typeof window === 'undefined') {
    return { position: DEFAULT_POSITION, mode: DEFAULT_MODE }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        position: parsed.position ?? DEFAULT_POSITION,
        mode: parsed.mode ?? DEFAULT_MODE,
      }
    }
  } catch {
    // corrupt data, ignore
  }
  return { position: DEFAULT_POSITION, mode: DEFAULT_MODE }
}

export function HeaderPrefsProvider({ children }: { children: ReactNode }) {
  const [position, setPositionState] = useState<HeaderPosition>(() => loadPrefs().position)
  const [mode, setModeState] = useState<HeaderMode>(() => loadPrefs().mode)

  // Persist to localStorage whenever prefs change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ position, mode }))
    } catch {
      // storage full or unavailable
    }
  }, [position, mode])

  const setPosition = useCallback((p: HeaderPosition) => {
    setPositionState(p)
    // Auto-derive mode from position
    const modeMap: Record<HeaderPosition, HeaderMode> = {
      top: 'top-expanded',
      bottom: 'bottom',
      floating: 'floating',
      minimized: 'minimized',
    }
    setModeState(modeMap[p])
  }, [])

  const setMode = useCallback((m: HeaderMode) => {
    setModeState(m)
  }, [])

  const reset = useCallback(() => {
    setPositionState(DEFAULT_POSITION)
    setModeState(DEFAULT_MODE)
  }, [])

  return (
    <HeaderPrefsContext.Provider value={{ position, setPosition, mode, setMode, reset }}>
      {children}
    </HeaderPrefsContext.Provider>
  )
}

export function useHeaderPrefs(): HeaderPrefsContextType {
  const ctx = useContext(HeaderPrefsContext)
  if (!ctx) {
    throw new Error('useHeaderPrefs must be used within HeaderPrefsProvider')
  }
  return ctx
}
